import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Alert,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddIcon from "@mui/icons-material/Add";
import { Delete, Edit } from "@mui/icons-material";
import api from "../../utils/api";
import NotificationModuleSettings from "./NotificationModuleSettings";
import StatusEmailTriggers from "./StatusEmailTriggers";

// ─── Module tab definitions ───────────────────────────────────────────────────
const MODULE_CONFIGS = [
  {
    label: "Work Orders",
    moduleId: 1,
    itemLabel: "work order",
    typeEndpoint: "/workorder-types",
    subtypeEndpoint: "/workorder-subtypes",
    typeLabelKey: "type",
    subtypeLabelKey: "subtype_name",
    subtypeParentKey: "type_id",
  },
  {
    label: "Violations",
    moduleId: 2,
    itemLabel: "violation",
    typeEndpoint: "/getCcrViolationTypes",
    subtypeEndpoint: null,
    typeLabelKey: "title",
  },
  {
    label: "Architectural Requests",
    moduleId: 3,
    itemLabel: "architectural request",
    typeEndpoint: "/architectural-request-types",
    subtypeEndpoint: "/architectural-request-subtypes",
    typeLabelKey: "type",
    subtypeLabelKey: "subtype_name",
    subtypeParentKey: "type_id",
  },
];

// ─── Shared style tokens ──────────────────────────────────────────────────────
const primaryButtonSx = {
  backgroundColor: "#2f63ad",
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 700,
  boxShadow: "none",
  "&:hover": { backgroundColor: "#1f4f94", boxShadow: "none" },
};

const secondaryButtonSx = {
  borderRadius: "10px",
  textTransform: "none",
  fontWeight: 600,
};

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    backgroundColor: "#fff",
  },
};

const dialogPaperSx = {
  borderRadius: "18px",
  background: "rgba(255,255,255,0.96)",
  backdropFilter: "blur(10px)",
  boxShadow: "0 20px 60px rgba(0,0,0,0.25)",
  overflow: "hidden",
};

const tablePaperSx = {
  borderRadius: "16px",
  border: "1px solid rgba(47,99,173,0.08)",
  boxShadow: "0 8px 24px rgba(0,0,0,0.06)",
  overflow: "hidden",
  backgroundColor: "rgba(255,255,255,0.96)",
};

// ─── Work Orders advanced routing (Special + Location) ────────────────────────
const WorkOrderAdvancedRouting = ({ showSnackbar }) => {
  const [workOrderTypes, setWorkOrderTypes] = useState([]);
  const [locations, setLocations] = useState([]);

  // Special notifications
  const [specials, setSpecials] = useState([]);
  const [specialForm, setSpecialForm] = useState({ property_id: "", workorder_type_id: "", email: "" });
  const [editingSpecialId, setEditingSpecialId] = useState(null);
  const [openSpecial, setOpenSpecial] = useState(false);

  // Location notifications
  const [locNotifs, setLocNotifs] = useState([]);
  const [locForm, setLocForm] = useState({ location_id: "", email: "" });
  const [editingLocId, setEditingLocId] = useState(null);
  const [openLoc, setOpenLoc] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get("/workorder-types"),
      api.get("/workorder-locations"),
      api.get("/specialNotifications"),
      api.get("/workorderLocationNotifications"),
    ]).then(([typesRes, locsRes, specialRes, locNotifsRes]) => {
      setWorkOrderTypes(typesRes.data);
      setLocations(locsRes.data);
      setSpecials(specialRes.data);
      setLocNotifs(locNotifsRes.data);
    }).catch(() => showSnackbar("Failed to load advanced routing data", "error"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchSpecials = () =>
    api.get("/specialNotifications").then((r) => setSpecials(r.data));
  const fetchLocNotifs = () =>
    api.get("/workorderLocationNotifications").then((r) => setLocNotifs(r.data));

  const getTypeName = (id) =>
    workOrderTypes.find((t) => String(t.id) === String(id))?.type ?? "N/A";
  const getLocation = (id) =>
    locations.find((l) => String(l.id) === String(id));

  // Special CRUD
  const openSpecialDialog = (item = null) => {
    setEditingSpecialId(item?.id ?? null);
    setSpecialForm(
      item
        ? { property_id: item.property_id, workorder_type_id: item.workorder_type_id, email: item.email }
        : { property_id: "", workorder_type_id: "", email: "" }
    );
    setOpenSpecial(true);
  };

  const submitSpecial = async () => {
    try {
      if (editingSpecialId) {
        await api.put(`/specialNotification/${editingSpecialId}`, specialForm);
        showSnackbar("Special routing updated");
      } else {
        await api.post("/specialNotification", specialForm);
        showSnackbar("Special routing added");
      }
      fetchSpecials();
      setOpenSpecial(false);
    } catch {
      showSnackbar("Failed to save special routing", "error");
    }
  };

  const deleteSpecial = async (id) => {
    if (!window.confirm("Delete this special routing rule?")) return;
    try {
      await api.delete(`/specialNotification/${id}`);
      showSnackbar("Deleted");
      fetchSpecials();
    } catch {
      showSnackbar("Failed to delete", "error");
    }
  };

  // Location CRUD
  const openLocDialog = (item = null) => {
    setEditingLocId(item?.id ?? null);
    setLocForm(
      item ? { location_id: item.location_id, email: item.email } : { location_id: "", email: "" }
    );
    setOpenLoc(true);
  };

  const submitLoc = async () => {
    try {
      if (editingLocId) {
        await api.put(`/workorderLocationNotification/${editingLocId}`, locForm);
        showSnackbar("Location routing updated");
      } else {
        await api.post("/workorderLocationNotification", locForm);
        showSnackbar("Location routing added");
      }
      fetchLocNotifs();
      setOpenLoc(false);
    } catch {
      showSnackbar("Failed to save location routing", "error");
    }
  };

  const deleteLoc = async (id) => {
    if (!window.confirm("Delete this location routing rule?")) return;
    try {
      await api.delete(`/workorderLocationNotification/${id}`);
      showSnackbar("Deleted");
      fetchLocNotifs();
    } catch {
      showSnackbar("Failed to delete", "error");
    }
  };

  return (
    <Box sx={{ mt: 3 }}>
      {/* Special Notifications */}
      <Accordion
        elevation={0}
        sx={{
          border: "1px solid rgba(47,99,173,0.10)",
          borderRadius: "14px !important",
          mb: 1.5,
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#223142", fontSize: "0.97rem" }}>
              Special Routing
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b778c" }}>
              Send to a specific email when a property submits a work order of a given type
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            sx={{ ...primaryButtonSx, mb: 2 }}
            onClick={() => openSpecialDialog()}
          >
            Add Special Routing
          </Button>

          <Paper elevation={0} sx={tablePaperSx}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Property ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>WO Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {specials.length > 0 ? specials.map((n) => (
                    <TableRow key={n.id}>
                      <TableCell>{n.id}</TableCell>
                      <TableCell>{n.property_id}</TableCell>
                      <TableCell>{getTypeName(n.workorder_type_id)}</TableCell>
                      <TableCell sx={{ wordBreak: "break-word" }}>{n.email}</TableCell>
                      <TableCell align="right">
                        <IconButton size="small" onClick={() => openSpecialDialog(n)}><Edit fontSize="small" /></IconButton>
                        <IconButton size="small" onClick={() => deleteSpecial(n.id)} color="error"><Delete fontSize="small" /></IconButton>
                      </TableCell>
                    </TableRow>
                  )) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: "text.secondary" }}>No special routing rules.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </AccordionDetails>
      </Accordion>

      {/* Location Notifications */}
      <Accordion
        elevation={0}
        sx={{
          border: "1px solid rgba(47,99,173,0.10)",
          borderRadius: "14px !important",
          "&:before": { display: "none" },
        }}
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.5 }}>
          <Box>
            <Typography sx={{ fontWeight: 700, color: "#223142", fontSize: "0.97rem" }}>
              Location Routing
            </Typography>
            <Typography variant="caption" sx={{ color: "#6b778c" }}>
              Route notifications to a specific email based on the work order's location
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2.5, pt: 0, pb: 2.5 }}>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            size="small"
            sx={{ ...primaryButtonSx, mb: 2 }}
            onClick={() => openLocDialog()}
          >
            Add Location Routing
          </Button>

          <Paper elevation={0} sx={tablePaperSx}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700 }}>ID</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Location</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Type</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Email</TableCell>
                    <TableCell sx={{ fontWeight: 700 }} align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {locNotifs.length > 0 ? locNotifs.map((n) => {
                    const loc = getLocation(n.location_id);
                    return (
                      <TableRow key={n.id}>
                        <TableCell>{n.id}</TableCell>
                        <TableCell>{loc ? loc.location_name : n.location_id}</TableCell>
                        <TableCell>{loc?.type ?? "N/A"}</TableCell>
                        <TableCell sx={{ wordBreak: "break-word" }}>{n.email}</TableCell>
                        <TableCell align="right">
                          <IconButton size="small" onClick={() => openLocDialog(n)}><Edit fontSize="small" /></IconButton>
                          <IconButton size="small" onClick={() => deleteLoc(n.id)} color="error"><Delete fontSize="small" /></IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  }) : (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ color: "text.secondary" }}>No location routing rules.</TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </AccordionDetails>
      </Accordion>

      {/* Special dialog */}
      <Dialog open={openSpecial} onClose={() => setOpenSpecial(false)} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.25rem", pt: 3, pb: 1 }}>
          {editingSpecialId ? "Edit Special Routing" : "Add Special Routing"}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <TextField
            label="Property ID"
            fullWidth
            value={specialForm.property_id}
            onChange={(e) => setSpecialForm({ ...specialForm, property_id: e.target.value })}
            sx={{ mt: 1.5, ...inputSx }}
            inputProps={{ maxLength: 20 }}
          />
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel>Work Order Type</InputLabel>
            <Select
              value={specialForm.workorder_type_id}
              label="Work Order Type"
              onChange={(e) => setSpecialForm({ ...specialForm, workorder_type_id: e.target.value })}
              sx={inputSx}
            >
              {workOrderTypes.map((t) => (
                <MenuItem key={t.id} value={t.id}>{t.type}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Email"
            fullWidth
            value={specialForm.email}
            onChange={(e) => setSpecialForm({ ...specialForm, email: e.target.value })}
            sx={{ mt: 2, ...inputSx }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
          <Button onClick={() => setOpenSpecial(false)} variant="outlined" sx={secondaryButtonSx}>Cancel</Button>
          <Button onClick={submitSpecial} variant="contained" sx={primaryButtonSx}>
            {editingSpecialId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Location dialog */}
      <Dialog open={openLoc} onClose={() => setOpenLoc(false)} fullWidth maxWidth="sm" PaperProps={{ sx: dialogPaperSx }}>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "1.25rem", pt: 3, pb: 1 }}>
          {editingLocId ? "Edit Location Routing" : "Add Location Routing"}
        </DialogTitle>
        <DialogContent sx={{ px: 3, pb: 1 }}>
          <FormControl fullWidth sx={{ mt: 1.5 }}>
            <InputLabel>Location</InputLabel>
            <Select
              value={locForm.location_id}
              label="Location"
              onChange={(e) => setLocForm({ ...locForm, location_id: e.target.value })}
              sx={inputSx}
            >
              {locations.map((l) => (
                <MenuItem key={l.id} value={l.id}>
                  {l.location_name}{l.type ? `. ${l.type}` : ""}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Email"
            fullWidth
            value={locForm.email}
            onChange={(e) => setLocForm({ ...locForm, email: e.target.value })}
            sx={{ mt: 2, ...inputSx }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, pt: 1.5, justifyContent: "center", gap: 1.25 }}>
          <Button onClick={() => setOpenLoc(false)} variant="outlined" sx={secondaryButtonSx}>Cancel</Button>
          <Button onClick={submitLoc} variant="contained" sx={primaryButtonSx}>
            {editingLocId ? "Update" : "Add"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

// ─── Main component ───────────────────────────────────────────────────────────
const EmailNotifications = () => {
  const [tab, setTab] = useState(0);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  return (
    <Paper
      elevation={0}
      sx={{
        maxWidth: 1100,
        mx: "auto",
        p: { xs: 2, md: 3 },
        borderRadius: "18px",
        border: "1px solid rgba(47,99,173,0.08)",
        boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
        backgroundColor: "rgba(255,255,255,0.96)",
      }}
    >
      <Typography variant="h5" sx={{ fontWeight: 700, color: "#223142", mb: 0.5 }}>
        Email Notifications
      </Typography>
      <Typography variant="body2" sx={{ color: "#6b778c", mb: 3 }}>
        Configure notification recipients, event triggers, and routing rules per module.
      </Typography>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{
          mb: 3,
          borderBottom: "1px solid #eef2f7",
          "& .MuiTab-root": { textTransform: "none", fontWeight: 600 },
        }}
      >
        {MODULE_CONFIGS.map((m) => (
          <Tab key={m.moduleId} label={m.label} />
        ))}
        <Tab label="Status Triggers" />
      </Tabs>

      {/* Module tabs 0-2 */}
      {MODULE_CONFIGS.map((cfg, idx) =>
        tab === idx ? (
          <Box key={cfg.moduleId}>
            <NotificationModuleSettings
              moduleId={cfg.moduleId}
              moduleName={cfg.label}
              itemLabel={cfg.itemLabel}
              typeEndpoint={cfg.typeEndpoint}
              subtypeEndpoint={cfg.subtypeEndpoint}
              typeLabelKey={cfg.typeLabelKey}
              subtypeLabelKey={cfg.subtypeLabelKey}
              subtypeParentKey={cfg.subtypeParentKey}
            />
            {/* Work Orders gets extra property/location routing panels */}
            {cfg.moduleId === 1 && <WorkOrderAdvancedRouting showSnackbar={showSnackbar} />}
          </Box>
        ) : null
      )}

      {/* Status Triggers tab */}
      {tab === MODULE_CONFIGS.length && <StatusEmailTriggers />}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Paper>
  );
};

export default EmailNotifications;
